import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, DollarSign, TrendingUp, AlertCircle } from "lucide-react";

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/owner/dashboard");
        setData(data);
      } catch (e) { if (typeof console !== 'undefined') console.debug('pages/owner/OwnerDashboard.jsx: swallowed', e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const k = data?.kpis || {};

  const cards = [
    { label: "Active Units", value: k.active_units || 0, icon: Building2, testid: "owner-kpi-units" },
    { label: "Monthly Rent", value: `$${(k.monthly_rent_gross || 0).toLocaleString()}`, icon: DollarSign, testid: "owner-kpi-rent" },
    { label: "YTD Distributions", value: `$${(k.ytd_distributions || 0).toLocaleString()}`, icon: TrendingUp, testid: "owner-kpi-ytd" },
    { label: "Past Due", value: `$${(k.past_due_amount || 0).toLocaleString()}`, icon: AlertCircle, danger: k.past_due_amount > 0, testid: "owner-kpi-pastdue" },
  ];

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      <PageHeader
        eyebrow="Portfolio"
        title="Your Properties"
        subtitle="Rent flowing in, expenses going out, distributions to you."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <Card key={c.label} className={`p-4 border-white/10 bg-card ${c.danger ? "border-rose-500/30" : ""}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <c.icon size={12} /> {c.label}
            </div>
            <div className={`text-2xl font-semibold mt-1 ${c.danger ? "text-rose-400" : ""}`} data-testid={c.testid}>
              {c.value}
            </div>
          </Card>
        ))}
      </div>

      {data?.latest_statement && (
        <Card className="p-4 border-emerald-500/30 bg-emerald-500/5" data-testid="latest-statement-card">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Latest Statement · {data.latest_statement.period}</div>
          <div className="mt-2 flex items-baseline gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Distribution</div>
              <div className="text-2xl font-semibold text-emerald-500">${data.latest_statement.distribution_amount.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Gross Rent</div>
              <div className="text-lg font-mono">${data.latest_statement.gross_income.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Expenses</div>
              <div className="text-lg font-mono text-rose-400">${data.latest_statement.total_expenses.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Status</div>
              <Badge variant={data.latest_statement.distribution_status === "sent" ? "default" : "secondary"}>
                {data.latest_statement.distribution_status}
              </Badge>
            </div>
          </div>
        </Card>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">
          Your leases · {data?.leases?.length || 0}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Monthly Rent</TableHead>
              <TableHead>Term</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!data?.leases || data.leases.length === 0) && (
              <TableRow className="border-white/5"><TableCell colSpan={4} className="text-center py-8 text-muted-foreground text-sm" data-testid="owner-leases-empty">
                No leases linked to your account yet. Your PM will attach them shortly.
              </TableCell></TableRow>
            )}
            {(data?.leases || []).map((l) => (
              <TableRow key={l.id} className="border-white/5" data-testid={`owner-lease-${l.id}`}>
                <TableCell className="font-medium">{l.unit}</TableCell>
                <TableCell className="text-right font-mono">${(l.monthly_rent || 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.start_date} → {l.end_date}</TableCell>
                <TableCell><Badge variant={l.status === "active" ? "default" : "secondary"}>{l.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
